<template>
  <div class="booking-participants">
    <div v-for="person in participants" :key="person.id" class="booking-participants__person">
      <div class="booking-participants__counter">
        <span class="booking-participants__counter--count">{{ person.id }}.</span>
        <div class="booking-participants__counter--title">
          <span>{{ person.title }}</span>
          <hr>
        </div>
      </div>
      <div class="booking-participants__form">
        <div class="booking-participants__form--row">
          <TextInput :name="`firstName_${person.id}`" placeholder="Wpisz imię (imiona) uczestnika z dokumentu"
            label="Imię (imiona)*" :firstLetterUppercase="true"
            @focus="onFocusTextInput" @blur="onBlurTextInput"
            data-vv-validate-on="change" data-vv-as="imię"
            v-model="person.firstName" :validateAs="`firstName_${person.id}`"
            @input="updateParticipantField(person.id - 1, 'firstName', $event)"
            v-validate="{ required: true, regex: /^[A-Za-zżźćńółęąśŻŹĆĄŚĘŁÓŃ]{2,}[A-Za-zżźćńółęąśŻŹĆĄŚĘŁÓŃ\s\-]{0,}$/ }" />
          <TextInput :name="`lastName_${person.id}`" placeholder="Wpisz nazwisko uczestnika z dokumentu"
            label="Nazwisko*" :firstLetterUppercase="true"
            @focus="onFocusTextInput" @blur="onBlurTextInput"
            data-vv-validate-on="change" data-vv-as="nazwisko"
            v-model="person.lastName" :validateAs="`lastName_${person.id}`"
            @input="updateParticipantField(person.id - 1, 'lastName', $event)"
            v-validate="{ required: true, regex: /^[A-Za-zżźćńółęąśŻŹĆĄŚĘŁÓŃ]{2,}[A-Za-zżźćńółęąśŻŹĆĄŚĘŁÓŃ\s\-]{0,}$/ }" />
        </div>
          
        <div class="booking-participants__form--row">
          <div class="booking-participants__form--column gender-list">
            <RadioListInputBox label="Płeć*" tabindex="0" :class="{
              'selection-box--blue-border': isMobile,
              'input-box--blue-border': !isMobile }"
              :highlight="dropdowns.gender[person.id]" :vvName="`gender_${person.id}`" :vvKey="`gender_${person.id}`"
              @click="toggle('gender', person.id)" @close="toggle('gender', person.id)"
              v-model="person.gender" :validateAs="`gender_${person.id}`" rules="required" :options="genderList"
              @input="updateParticipantField(person.id - 1, 'gender', $event)"
            />
          </div>
          
          <div class="booking-participants__form--column">
            <DateInput :name="`birthday_${person.id}`" title="Data urodzenia" :key="`birthday_${person.id}`"
              label="Data urodzenia*" useNativeDateInput placeholder="DD-MM-RRRR" tabindex="0"
              :min="person.type == 'adult' ? minAdultDate : minChildDate"
              :max="person.type == 'adult' ? maxAdultDate : maxChildDate"
              :editable="person.type == 'adult'" @validateNonEditable="validateNonEditableField(person.id)"
              v-model="person.birthday" :validateAs="`birthday_${person.id}`"
              @input="updateParticipantField(person.id - 1, 'birthday', $event)"
              v-validate="'moment_not_zero'"
              :class="{
                'date-input--blue-border': person.type == 'adult',
                'date-input--child': person.type != 'adult',
                'field-invalid': person.type != 'adult' && dropdowns.manualChildValidation[person.id],
                'field-valid': person.type != 'adult' && person.birthday && !person.birthday.isZero(),
              }">

              <div slot="not-editable-field" v-if="dropdowns.manualChildValidation[person.id] && person.type != 'adult'" class="v-field-error v-not-editable">
                Aby zmienić datę urodzenia dziecka musisz <span @click="goBackToOffer">WRÓCIĆ DO OFERTY.</span>
              </div>
              <Icon slot="icon" v-if="person.type == 'adult'" icon="calendar" class="calendar-booking-client" />
            </DateInput>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Adult age validation error message -->
    <div v-if="dropdowns.adultAgeValidation" class="booking-participants__adult-age-error">
      <div class="v-field-error">
        Przynajmniej jedna osoba musi mieć ukończone 18 lat
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'
import { createHelpers } from 'vuex-map-fields'

import ParticipantsLogic from '@/components/booking/logic/Participants'
import DateInput from '@/components/DateInput'
import TextInput from '@/components/forms/fields/TextInput.vue'
import Icon from '@/components/Icon.vue'
import RadioListInputBox from '@/components/RadioListInputBox'
import DropdownHandler from '@/components/search/DropdownHandler'
import Store from '@/store/index'
import MediaDetector from '@/utils/MediaDetector'
import FormStorageMixin from '@/mixins/FormStorageMixin'
import BookingParticipantsService from '@/services/BookingParticipantsService'

const { mapMultiRowFields } = createHelpers({
  getterType: 'Booking/getField',
  mutationType: 'Booking/updateField',
})

@Component({
  store: Store,
  mixins: [
    MediaDetector,
    DropdownHandler,
    ParticipantsLogic,
    FormStorageMixin,
  ],
  components: {
    TextInput,
    RadioListInputBox,
    DateInput,
    Icon,
  },
  props: {
    data: {
      type: Object,
      required: false,
    },
    user: {
      type: Object,
      required: false,
    },
    genderList: {
      type: Array,
      required: false,
    },
    backOfferForm: {
      type: HTMLFormElement,
      required: false,
    },
    additionalFields: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapMultiRowFields([ 'participants' ]),
  },
  watch: {
    additionalFields: {
      immediate: true,
      handler (val) {
        const storedData = this.loadFromStorage() || {}
        
        if (val && storedData.clientForm) {
          // Update first participant data from client form
          const fields = [ 'firstName', 'lastName', 'gender', 'birthday' ]
          fields.forEach(field => {
            const clientFormValue = storedData.clientForm[field]
            const fieldName = `${field}_1`

            if (field === 'birthday') {
              const birthdayMoment = moment(clientFormValue)
              if (birthdayMoment.isValid() && !birthdayMoment.isSame(moment(0))) {
                if (this.participants[0]) {
                  this.$set(this.participants[0], field, birthdayMoment)
                  this.$validator.validate(fieldName, birthdayMoment)
                }
              }
            } else if (clientFormValue) {
              if (this.participants[0]) {
                this.$set(this.participants[0], field, clientFormValue)
                this.$validator.validate(fieldName, clientFormValue)
              }
            }
          })

          // Force component update
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
      },
    },
    '$root.$data.storage': {
      deep: true,
      handler (newStorage) {
        if (this.additionalFields && newStorage && newStorage.clientForm) {
          const clientForm = newStorage.clientForm
          const fields = [ 'firstName', 'lastName', 'gender', 'birthday' ]
          
          fields.forEach(field => {
            if (this.participants[0] && clientForm[field]) {
              const value = field === 'birthday' ? moment(clientForm[field]) : clientForm[field]
              if (JSON.stringify(this.participants[0][field]) !== JSON.stringify(value)) {
                this.$set(this.participants[0], field, value)
              }
            }
          })
        }
      },
    },
    participants: {
      handler (newVal) {
        if (!newVal || !Array.isArray(newVal)) return

        // Get current storage data
        const currentData = this.loadFromStorage() || {}
        const participantsData = newVal.map(participant => ({
          id: participant.id,
          type: participant.type || 'adult',
          firstName: participant.firstName || '',
          lastName: participant.lastName || '',
          gender: participant.gender || '',
          birthday: participant.birthday && !participant.birthday.isZero() ? 
            participant.birthday.format('YYYY-MM-DD') : '',
          title: participant.title || '',
        }))

        // Update storage with new data
        this.saveToStorage({
          ...currentData,
          participants: participantsData,
        })

        // Sync first participant with client form if needed
        if (participantsData[0]) {
          const clientForm = currentData.clientForm || {}
          const firstParticipant = participantsData[0]
          
          // Only update if values are different
          if (firstParticipant.firstName !== clientForm.firstName ||
              firstParticipant.lastName !== clientForm.lastName ||
              firstParticipant.gender !== clientForm.gender ||
              firstParticipant.birthday !== clientForm.birthday) {
            
            this.saveToStorage({
              ...currentData,
              participants: participantsData,
              clientForm: {
                ...clientForm,
                firstName: firstParticipant.firstName,
                lastName: firstParticipant.lastName,
                gender: firstParticipant.gender,
                birthday: firstParticipant.birthday,
              },
            })
          }
        }

        // Update the service
        this.dx.participants = new BookingParticipantsService(this.data, participantsData)
        
        // Only validate if this is a user-initiated change, not data loading
        // Check if we have actual birthday data that needs validation
        const hasValidBirthdays = participantsData.some(p => p.birthday && p.birthday !== '')
        if (hasValidBirthdays && this._isMounted) {
          this.$nextTick(() => {
            this.validateAdultAgeRequirement()
          })
        }
      },
      deep: true,
    },
    user: {
      deep: true,
      immediate: true,
      handler (newUser) {
        if (newUser && this.additionalFields) {
          const firstParticipant = {
            ...this.participants[0],
            firstName: newUser.firstName || this.participants[0].firstName,
            lastName: newUser.lastName || this.participants[0].lastName,
            gender: newUser.gender || this.participants[0].gender,
            birthday: newUser.birthday ? moment(newUser.birthday) : this.participants[0].birthday,
          }

          this.$set(this.participants, 0, firstParticipant)
          
          // Wait for fields to be attached before validation
          this.$nextTick(() => {
            const fields = [ 'firstName', 'lastName', 'gender', 'birthday' ]
            fields.forEach(field => {
              const fieldName = `${field}_1`
              // Clear any existing errors
              this.$validator.errors.remove(fieldName)
              
              // Only validate if we have a value
              if (newUser[field]) {
                const value = field === 'birthday' ? moment(newUser[field]) : newUser[field]
                // Ensure field is attached before validating
                if (this.$validator.fields.find({ name: fieldName })) {
                  this.$validator.validate(fieldName, value)
                }
              }
            })
          })
        }
      },
    },
  },
})
export default class BookingParticipants extends Vue {
  minAdultDate = moment().subtract(100, 'years').startOf('day') 
  minChildDate = moment().subtract(3, 'years').startOf('day')
  maxAdultDate = moment().subtract(18, 'years').startOf('day')
  maxChildDate = moment().startOf('day')

  dropdowns = {
    gender: [],
    manualChildValidation: [],
    adultAgeValidation: false,
  }

  _isMounted = false

  mounted () {
    this.dropdowns.gender = [ false, ...this.participants ].map(() => false)
    this.dropdowns.manualChildValidation = [ false, ...this.participants ].map(() => false)
    this.initializeParticipants()
    
    // Mark as mounted after initialization
    this._isMounted = true
    
    // Only validate after a short delay to allow data loading to complete
    this.$nextTick(() => {
      setTimeout(() => {
        this.validateAdultAgeRequirement()
      }, 100)
    })
  }

  // Expose validation method for parent components
  validateParticipants () {
    this.validateAllFields()
    return this.validateAdultAgeRequirement()
  }

  formatParticipantData (participant) {
    if (!participant || Object.keys(participant).length === 0) return {}

    const formatted = {
      id: participant.id,
      type: participant.type || 'adult',
    }

    if (participant && participant.firstName) formatted.firstName = participant.firstName
    if (participant && participant.lastName) formatted.lastName = participant.lastName
    if (participant && participant.gender) formatted.gender = participant.gender
    if (participant && participant.birthday) {
      formatted.birthday = moment(participant.birthday)
    }

    return formatted
  }

  initializeParticipants () {
    const storedData = this.loadFromStorage()
    if (storedData && storedData.participants && storedData.participants.length) {
      // Check if participant counts match the offer
      if (this.data && this.data.Persons) {
        const offerAdults = this.data.Persons.filter(p => p.Gender !== 'CHD' && p.Gender !== 'INF').length
        const offerChildren = this.data.Persons.filter(p => p.Gender === 'CHD' || p.Gender === 'INF').length
        
        const storedAdults = storedData.participants.filter(p => p.type === 'adult').length
        const storedChildren = storedData.participants.filter(p => p.type === 'child' || p.type === 'infant').length

        // If counts don't match, clear participants from storage
        if (storedAdults !== offerAdults || storedChildren !== offerChildren) {
          const newStoredData = { ...storedData }
          delete newStoredData.participants
          this.saveToStorage(newStoredData)
          return
        }
      }

      const participants = storedData.participants.map(p => ({
        ...p,
        birthday: p.birthday ? moment(p.birthday) : moment(0),
        type: p.type || 'adult',
        title: p.title || 'Osoba dorosła',
      }))

      // Update participants directly without using store
      this.participants = participants
      this.dx.participants = new BookingParticipantsService(this.data, participants)
      
      // Validate after loading stored data, but only if we have meaningful data
      const hasValidBirthdays = participants.some(p => p.birthday && !p.birthday.isZero())
      if (hasValidBirthdays) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.validateAdultAgeRequirement()
          }, 50)
        })
      }
    }
  }

  validateNonEditableField (id) {
    this.toggle('manualChildValidation', id)
  }

  manualFieldsValidation () {
    if (this.user.firstName) {
      this.$validator.flag('firstName_1', { changed: true })
      this.$validator.validate('firstName_1', this.user.firstName)
    }

    if (this.user.lastName) {
      this.$validator.flag('lastName_1', { changed: true })
      this.$validator.validate('lastName_1', this.user.lastName)
    }

    if (this.user.birthday) {
      this.$validator.flag('birthday_1', { changed: true })
      this.$validator.validate('birthday_1', this.user.birthday)
    }
  }

  hasAtLeastOneAdult () {
    const adultsOver18 = this.participants.filter(participant => {
      if (!participant.birthday || participant.birthday.isZero()) {
        return false
      }
      
      const age = moment().diff(participant.birthday, 'years')
      const isAdult = age >= 18
      
      return isAdult
    })
    
    return adultsOver18.length > 0
  }

  validateAdultAgeRequirement () {
    const hasAdult = this.hasAtLeastOneAdult()
    
    if (!hasAdult) {
      this.dropdowns.adultAgeValidation = true
      
      // Add a custom validation error
      this.$validator.errors.add({
        field: 'adult_age_requirement',
        msg: 'Przynajmniej jedna osoba musi mieć ukończone 18 lat',
      })
      
      return false
    } else {
      this.dropdowns.adultAgeValidation = false
      
      // Remove the custom validation error if it exists
      this.$validator.errors.remove('adult_age_requirement')
      
      return true
    }
  }

  updateParticipantField (index, field, value) {
    const currentData = this.loadFromStorage() || {}
    
    // Update participant
    if (this.participants[index]) {
      const oldValue = this.participants[index][field]
      this.participants[index][field] = value
      
      // If birthday field is updated and the value actually changed, validate adult age requirement
      if (field === 'birthday' && this._isMounted) {
        const oldBirthday = moment.isMoment(oldValue) ? oldValue.format('YYYY-MM-DD') : oldValue
        const newBirthday = moment.isMoment(value) ? value.format('YYYY-MM-DD') : value
        
        // Only validate if the birthday actually changed and is not empty/zero
        if (oldBirthday !== newBirthday && value && !moment(value).isZero()) {
          this.$nextTick(() => {
            this.validateAdultAgeRequirement()
          })
        }
      }
      
      // If this is the first participant and additionalFields is true, sync with ClientForm
      if (index === 0 && currentData.clientForm && currentData.clientForm.additionalFields) {
        const formattedValue = field === 'birthday' ? 
          (moment.isMoment(value) ? value.format('YYYY-MM-DD') : moment(value).format('YYYY-MM-DD')) : 
          value

        // Update storage with both clientForm and participants data
        this.saveToStorage({
          ...currentData,
          clientForm: {
            ...currentData.clientForm,
            [field]: formattedValue,
            additionalFields: true,
          },
          participants: this.participants.map(p => ({
            ...p,
            birthday: p.birthday ? p.birthday.format('YYYY-MM-DD') : null,
          })),
        })

        // Changed from update-client-form to update-user-field to match template
        this.$emit('update-user-field', {
          field,
          value: field === 'birthday' ? moment(value) : value,
        })
        
        return
      }
      
      // For other participants or when additionalFields is false
      this.saveToStorage({
        ...currentData,
        participants: this.participants.map(p => ({
          ...p,
          birthday: p.birthday ? p.birthday.format('YYYY-MM-DD') : null,
        })),
      })
    }
  }

  validateAllFields () {
    // First validate individual participant fields
    this.participants.forEach(participant => {
      const fields = [ 'firstName', 'lastName', 'gender', 'birthday' ]
      fields.forEach(field => {
        const fieldName = `${field}_${participant.id}`
        const value = participant[field]
        
        // Skip validation for first participant if additionalFields is true
        if (participant.id === 1 && this.additionalFields) {
          this.$validator.flag(fieldName, { valid: true, invalid: false })
          return
        }

        if (field === 'birthday') {
          // Check if we have a valid birthday string or moment object
          if ((typeof value === 'string' && value) || 
              (moment.isMoment(value) && !value.isZero())) {
            this.$validator.flag(fieldName, { valid: true, invalid: false })
          } else {
            this.$validator.validate(fieldName, '')
          }
        } else if (value) {
          this.$validator.flag(fieldName, { valid: true, invalid: false })
        } else {
          this.$validator.validate(fieldName, '')
        }
      })
    })
    
    // Then validate adult age requirement
    this.validateAdultAgeRequirement()
  }

  goBackToOffer () {
    if (this.backOfferForm) this.backOfferForm.submit()
  }

  onFocusTextInput (e) {
    return e.target.closest('.text-input').classList.add('text-input--focus')
  }

  onBlurTextInput (e) {
    return e.target.closest('.text-input').classList.remove('text-input--focus')
  }
}
</script>

<style lang="scss" scoped>
.booking-participants {
  display: grid;
  grid-template-columns: 1fr;

  @include mediaFluid(tablet) {
    grid-row-gap: 20px;
  }

  &__person {
    display: grid;
    grid-template-columns: 1fr;

    @include mediaFluid(tablet) {
      grid-template-columns: 152px 1fr;
    }

    @include mediaFluid(desktop) {
      grid-template-columns: 190px 1fr;
    }
  }

  &__counter {
    background-color: #005CA9;
    color: #fff;
    text-transform: uppercase;
    font-family: 'Gotham';
    padding: 20px;
    display: grid;
    grid-template-columns: 40px 1fr;
    align-items: center;
    justify-content: left;

    @include mediaFluid(tablet) {
      padding: 30px 20px;
      grid-template-columns: 1fr;
      align-content: flex-end;
    }

    @include mediaFluid(desktop) {
      grid-row-gap: 5px;
    }

    &--count {
      font-weight: bold;
      opacity: .6;
      font-size: 2.1em;
    }

    &--title {
      display: flex;
      flex-direction: column;
      font-size: 11px;
    }

    hr {
      display: inline-block;
      width: 20px;
      height: 3px;
      background-color: #fff;
      border: none;
      margin: 5px 0 0;
    }
  }

  &__form {
    display: grid;
    grid-row-gap: 15px;
    padding: 26px 20px;
    background-color: #fff;

    @include mediaFluid(tablet) {
      grid-row-gap: 22px;
    }

    @include mediaFluid(desktop) {
      padding-bottom: 31px;
    }
  }

  &__form--row {
    display: grid;
    grid-template-columns: 1fr;
    grid-column-gap: 20px;
    grid-row-gap: 17px;

    @include mediaFluid(tablet) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__form--column {
    display: grid;
    grid-column-gap: 20px;
    align-items: flex-start;
  }

  &__adult-age-error {
    background-color: #fff;
    padding: 20px;
    margin-top: 10px;
    border-left: 4px solid #e74c3c;

    .v-field-error {
      color: #e74c3c;
      font-size: 14px;
      font-weight: 500;
      margin: 0;
    }

    @include mediaFluid(tablet) {
      margin-top: 0;
    }
  }
}
</style>

<style lang="scss">
.booking-participants .date-input--child .date-input-native__icon {
  display: none;
}
</style>


// WEBPACK FOOTER //
// src/components/booking/Participants.vue